import { Button } from "@/projects/NPS/Stepper/common/button";
import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect } from "react";

export const AlternatingFeatures = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const features = [
    {
      id: "IPO",
      title: "Invest in IPO",
      desc: "Get early access to companies going public. Invest in Initial Public Offerings and be part of the growth journey from day one with comprehensive research and analysis.",
      img: "/assets/IPO.jpg",
      color: "blue",
      bulletPoints: [
        "Early access to new listings",
        "Detailed company analysis",
        "Easy application process",
      ],
      bgColorClass: "bg-blue-100",
      buttonText: "Explore IPOs",
    },
    {
      id: "Bonds",
      title: "Invest in Bonds",
      desc: "Secure your portfolio with high-quality bonds. Access AAA-rated government and corporate bonds with attractive yields and predictable returns",
      img: "/assets/Bonds.jpg",
      color: "green",
      bulletPoints: [
        "AAA-rated securities",
        "Fixed income returns",
        "Portfolio diversification",
      ],
      bgColorClass: "bg-green-100",
      buttonText: "Explore Bonds",
    },
    {
      id: "Fixed Deposits",
      title: "Invest in Fixed Deposits",
      desc: "Park your savings in DICGC-insured fixed deposits from trusted banks and NBFCs. Earn up to 9.10% interest with complete capital safety.",
      img: "/assets/FD.jpg",
      color: "yellow",
      bulletPoints: [
        "Up to 9.10% interest",
        "DICGC insured",
        "Flexible tenures",
      ],
      bgColorClass: "bg-yellow-100",
      buttonText: "Explore Fixed Deposits",
    },
    {
      id: "NPS",
      title: "Invest in NPS",
      desc: "Secure your retirement with National Pension System. Enjoy tax benefits under Section 80C and 80CCD while building a substantial retirement corpus.",
      img: "/assets/NPS.jpg",
      color: "purple",
      bulletPoints: [
        "Tax benefits up to ₹2 lakh",
        "Flexible contributions",
        "Professional fund management",
      ],
      bgColorClass: "bg-purple-100",
      buttonText: "Explore NPS",
    },
    {
      id: "Stock Analytics",
      title: "Invest in Stock Analytics",
      desc: "Make informed investment decisions with our comprehensive stock analytics tools. Access detailed financial data, charts, and expert insights.",
      img: "/assets/Stock.jpg",
      color: "orange",
      bulletPoints: ["Real-time data", "Technical analysis", "Expert insights"],
      bgColorClass: "bg-orange-100",
      buttonText: "Explore Stock Analytics",
    },
  ];

  return (
    <section className="w-full">
      {features.map((f, i) => (
        <section
          key={f.id}
          className={`w-full py-14 md:py-20 ${
            i % 2 === 1 ? "bg-[rgba(238,240,241,0.25)]" : ""
          }`}
        >
          <div
            data-aos={i % 2 === 0 ? "fade-right" : "fade-left"}
            className={`container mx-auto flex max-w-7xl flex-col items-center gap-12 px-4 sm:px-6 md:gap-16 md:px-10 lg:flex-row lg:gap-20 ${i % 2 === 1 ? "lg:flex-row-reverse" : ""} `}
          >
            {/* IMAGE SECTION */}
            <div className="group relative flex w-full justify-center lg:flex-1">
              {/* Decorative Shape */}
              <div
                className={`// slightly taller for nice overlap absolute inset-0 left-1/2 top-1/2 h-[110%] w-[100%] -translate-x-1/2 -translate-y-1/2 rotate-3 rounded-3xl ${f.bgColorClass} transition-transform duration-500 group-hover:-rotate-6`}
              />

              {/* Zoom Image Card */}
              <div className="relative w-4/5 overflow-hidden rounded-3xl bg-white p-4 shadow-xl sm:w-3/5 md:w-1/2 lg:w-3/4">
                <img
                  src={f.img}
                  alt={f.title}
                  className="h-full w-full rounded-2xl object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* TEXT SECTION */}
            <div
              className="flex-1 space-y-6 text-center lg:text-left"
              data-aos="fade-up"
            >
              {/* Badge */}
              <div className="inline-flex rounded-full bg-[rgba(10,58,77,0.1)] px-3 py-1.5 text-xs font-semibold text-[#0A3A4D]">
                {f.id}
              </div>

              <h3 className="text-3xl font-bold leading-tight text-[#0A344A] md:text-4xl">
                {f.title}
              </h3>

              <p className="mx-auto max-w-xl text-lg leading-relaxed text-muted-foreground lg:mx-0">
                {f.desc}
              </p>

              {/* Bullet Points */}
              <ul className="mx-auto space-y-2 text-center lg:mx-0 lg:text-left">
                {f.bulletPoints.map(point => (
                  <li
                    key={point}
                    className="flex items-center justify-center gap-2 lg:justify-start"
                  >
                    <span className="mt-1 h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-base text-[#2A3D47]">{point}</span>
                  </li>
                ))}
              </ul>

              {/* Button */}
              <Button
                type="button"
                className="items-cente f ocus-visible:ring-offset-2 inline-flex h-12 justify-center gap-2 whitespace-nowrap rounded-xl bg-npsText px-8 py-3 font-nunito text-base font-bold text-white shadow-lg ring-offset-background transition-all duration-200 hover:bg-npsText/90 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 md:py-4 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                role="button"
              >
                {f.buttonText}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-right h-4 w-4"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </Button>
            </div>
          </div>
        </section>
      ))}
    </section>
  );
};
